import { useEffect, useRef } from 'react'
import { useQuery } from 'convex/react'
import { useRouter, usePathname } from 'expo-router'
import Toast from 'react-native-toast-message'
import { api } from '@ppwr/convex/_generated/api'
import { useStore } from '@/models'
import { openLobby } from '@/screens/tournament/mttNavigation'
import { TOURNAMENT_TOAST_TIMER } from '@ppwr/convex/shared/tournamentConstants'

const ONE_MIN_MS = 60 * 1000

function useTournamentCountdownToasts() {
  const userId = useStore().session.user?.auth0Id
  const pathname = usePathname()
  const router = useRouter()

  const shownRef = useRef(new Set<string>())
  const prevStatesRef = useRef<Record<string, string>>({})
  const tournamentsRef = useRef<ReturnType<typeof useQuery<typeof api.tournaments.queries.getTournamentsForUser>>>(undefined)
  const pathnameRef = useRef(pathname)
  const routerRef = useRef(router)

  const tournaments = useQuery(
    api.tournaments.queries.getTournamentsForUser,
    userId ? { userId } : 'skip'
  )

  // Keep refs current so the interval can read latest values without stale closures
  useEffect(() => { tournamentsRef.current = tournaments }, [tournaments])
  useEffect(() => { pathnameRef.current = pathname }, [pathname])
  useEffect(() => { routerRef.current = router }, [router])

  // Reactively detect tournament start (state → 'active')
  useEffect(() => {
    if (!tournaments) return
    for (const t of tournaments) {
      const prev = prevStatesRef.current[t._id]
      prevStatesRef.current[t._id] = t.state

      if (prev !== undefined && prev !== 'active' && t.state === 'active') {
        const key = `${t._id}-started`
        if (!shownRef.current.has(key) && !pathnameRef.current.startsWith('/mtt')) {
          shownRef.current.add(key)
          const tournamentId = t._id
          const name = t.name
          Toast.show({
            type: 'ppToast',
            text1: `${name} is starting`,
            visibilityTime: 8000,
            onPress: () => openLobby(routerRef.current, tournamentId)
          })
        }
      }
    }
  }, [tournaments])

  // Interval-based time checks (5 min and 1 min warnings)
  const hasTournaments = (tournaments?.length ?? 0) > 0
  useEffect(() => {
    if (!hasTournaments) return

    const check = () => {
      const ts = tournamentsRef.current
      if (!ts) return
      const now = Date.now()

      for (const t of ts) {
        if (t.state !== 'registration') continue
        if (pathnameRef.current.startsWith('/mtt')) continue

        const timeUntilStart = t.scheduledStartTime - now
        const tournamentId = t._id
        const name = t.name

        // 5-minute warning: fire when between 4:00 and 6:00 remaining
        const key5 = `${tournamentId}-5min`
        if (!shownRef.current.has(key5) && timeUntilStart > 4 * ONE_MIN_MS && timeUntilStart <= 6 * ONE_MIN_MS) {
          shownRef.current.add(key5)
          Toast.show({
            type: 'ppToast',
            text1: `${name} starts in 5 minutes`,
            visibilityTime: 6000,
            onPress: () => openLobby(routerRef.current, tournamentId)
          })
        }

        // 1-minute warning: fire when between 0:30 and 1:30 remaining
        const key1 = `${tournamentId}-1min`
        if (!shownRef.current.has(key1) && timeUntilStart > 30_000 && timeUntilStart <= 90_000) {
          shownRef.current.add(key1)
          Toast.show({
            type: 'ppToast',
            text1: `${name} starts in 1 minute`,
            visibilityTime: 6000,
            onPress: () => openLobby(routerRef.current, tournamentId)
          })
        }
      }
    }

    check()
    const id = setInterval(check, TOURNAMENT_TOAST_TIMER)
    return () => clearInterval(id)
  }, [hasTournaments])
}

export function TournamentCountdownToasts() {
  useTournamentCountdownToasts()
  return null
}
